import { SCOPE_BOXES } from "@/api/access/access.types";
import type { RoleScopeKinds, ScopeBox, ScopeKind } from "@/api/access/access.types";
import type { ScopeWrite } from "@/api/access/userScope";
import type { UserScopeCompany } from "@/api/access/userScope.types";
import type { CompanyRole } from "@/api/roles-permissions/rolesIn";
import {
  boxesInPlay,
  incompleteRules,
  planRuleWrites,
  rulesFromScope,
  unfilledRequired,
  type ScopeRule,
} from "@/components/features/RolesPermissions/SetScopeModal/scopeRules";

/**
 * Stands in a clients row's values for "every client in this entity". The access engine stores
 * that as the clients assignment `mode: "all"`, not as a list, so it never reaches a rule write.
 */
export const ALL_CLIENTS = "__all_clients__";

/**
 * Super Admin is a kind of account, not a role (`userType: super_admin`, created by its own
 * route), but it is picked from the roles list (Jesus, 2026-10-28). The form carries it as this id.
 */
export const SUPER_ADMIN_ROLE_ID = "super_admin";

/** Super Admin can do everything, so picking it clears the other roles and picking one clears it. */
export const withSuperAdminAlone = (before: string[], next: string[]): string[] =>
  next.includes(SUPER_ADMIN_ROLE_ID) && !before.includes(SUPER_ADMIN_ROLE_ID)
    ? [SUPER_ADMIN_ROLE_ID]
    : next.filter((id) => id !== SUPER_ADMIN_ROLE_ID);

/** Each company has its own copy of a role, matched as Add a Role matches them: code, else name. */
const roleKey = (role: Pick<CompanyRole, "code" | "name">) =>
  role.code ?? role.name.trim().toLowerCase();

export type GivenRole = { roleId: string; entityId: string | null };

/**
 * UI26: at the ServEngine level the picker lists the platform company's roles. One whose level
 * counts at the main domain is given as picked. An entity-level one counts only at its own
 * company's link (contract 4.3, D38), so the platform's copy would apply nowhere: it is given as
 * each picked entity's own copy instead, and an entity without one is named in `missingIn`.
 */
export const givenAsEntityCopies = (
  roleId: string,
  servEngineRoles: CompanyRole[],
  rolesByEntity: Record<string, CompanyRole[]>,
): { given: GivenRole[]; missingIn: string[] } => {
  const picked = servEngineRoles.find((role) => role.id === roleId);
  if (!picked || picked.level !== "entity")
    return { given: [{ roleId, entityId: null }], missingIn: [] };

  const given: GivenRole[] = [];
  const missingIn: string[] = [];
  for (const [entityId, roles] of Object.entries(rolesByEntity)) {
    const copy = roles.find((role) => role.level === "entity" && roleKey(role) === roleKey(picked));
    if (copy) given.push({ roleId: copy.id, entityId });
    else missingIn.push(entityId);
  }
  return { given, missingIn };
};

export type ServEngineRolePlan = {
  assignedRoleIds: string[];
  /** One grant per copy; `pickedRoleId` is the row's own pick, for naming it. */
  temporaryRoles: Array<GivenRole & { endsOn: string; pickedRoleId: string }>;
  missing: Array<{ roleId: string; entityId: string }>;
};

/** New User's picks at the ServEngine level, each given as `givenAsEntityCopies` says (UI26). */
export const planServEngineRoles = (
  assignedRoleIds: string[],
  temporaryRoles: Array<{ roleId: string; endsOn: string }>,
  resolve: (roleId: string) => { given: GivenRole[]; missingIn: string[] },
): ServEngineRolePlan => {
  const missing: ServEngineRolePlan["missing"] = [];
  const give = (roleId: string) => {
    const { given, missingIn } = resolve(roleId);
    missing.push(...missingIn.map((entityId) => ({ roleId, entityId })));
    return given;
  };
  const assigned = assignedRoleIds.flatMap((roleId) => give(roleId).map((copy) => copy.roleId));
  const temporary = temporaryRoles.flatMap((row) =>
    give(row.roleId).map((copy) => ({ ...copy, endsOn: row.endsOn, pickedRoleId: row.roleId })),
  );
  return { assignedRoleIds: assigned, temporaryRoles: temporary, missing };
};

export type RoleScopeSource = { roleName: string; scopeKinds: RoleScopeKinds };

const KIND_WEIGHT: Record<ScopeKind, number> = { not_used: 0, optional: 1, required: 2 };

/**
 * What a person who does not exist yet will be asked for in one entity: the boxes their roles
 * put in play, pooled the way the engine pools them (the strongest kind any role asks for).
 */
export function companyForNewUser(
  tenantId: string,
  tenantName: string,
  roles: RoleScopeSource[],
): UserScopeCompany {
  const kindOf = (box: ScopeBox): ScopeKind =>
    roles.reduce<ScopeKind>((strongest, role) => {
      const kind = role.scopeKinds[box] ?? "not_used";
      return KIND_WEIGHT[kind] > KIND_WEIGHT[strongest] ? kind : strongest;
    }, "not_used");

  return {
    tenantId,
    tenantName,
    boxes: SCOPE_BOXES.map((box) => {
      const kind = kindOf(box);
      return {
        box,
        kind: kind === "not_used" ? "not_in_play" : kind,
        required: kind === "required",
        emptyRequired: false,
      };
    }),
    clients: {
      assignment: null,
      teamClientIds: [],
      rule: null,
      effective: { kind: "only", clientIds: [] },
    },
    rules: {},
  };
}

/** The roles that make a box required, named in the row's error ("Team Lead needs clients…"). */
export const rolesRequiring = (roles: RoleScopeSource[], box: ScopeBox): string[] =>
  roles.filter((role) => role.scopeKinds[box] === "required").map((role) => role.roleName);

/**
 * Keeps what the user already set when the roles change on step 1: rows on boxes still in play
 * stay, rows on boxes no role uses any more go, and a newly required box gets its row.
 */
export function reconcileRules(company: UserScopeCompany, current: ScopeRule[]): ScopeRule[] {
  const inPlay = new Set(boxesInPlay(company));
  const required = new Set(
    company.boxes.filter((entry) => entry.required).map((entry) => entry.box),
  );
  const kept = current
    .filter((rule) => rule.box === null || inPlay.has(rule.box))
    .map((rule) => {
      const stillRequired =
        rule.kind === "inclusion" && rule.box !== null && required.has(rule.box);
      // "All clients" is offered on the required row only, so it cannot stay on a row that is not.
      const ids = stillRequired ? rule.ids : rule.ids.filter((id) => id !== ALL_CLIENTS);
      return { ...rule, required: stillRequired, ids };
    });
  const seeded = rulesFromScope(company).filter(
    (seed) => !kept.some((rule) => rule.box === seed.box && rule.kind === seed.kind),
  );
  return [...kept, ...seeded];
}

/** Rows that stop Create User: a required box left empty, or a limit started and not finished. */
export const blockingRules = (rules: ScopeRule[]): ScopeRule[] => [
  ...unfilledRequired(rules),
  ...incompleteRules(rules),
];

/**
 * The scope requests for a new person in one entity. "All clients" becomes the `mode: "all"`
 * assignment, written last as stage 8's planner writes its own assignment last, so a failure
 * part-way leaves the person with less than chosen rather than more.
 */
export function planNewUserWrites(company: UserScopeCompany, rules: ScopeRule[]): ScopeWrite[] {
  const allClients = rules.some(
    (rule) => rule.box === "clients" && rule.kind === "inclusion" && rule.ids.includes(ALL_CLIENTS),
  );
  const listed = rules.filter(
    (rule) => !(rule.box === "clients" && rule.kind === "inclusion" && allClients),
  );
  const writes = planRuleWrites(company, listed);
  return allClients ? [...writes, { op: "setClients", mode: "all", clientIds: [] }] : writes;
}
